import {
  DEFAULT_ANOMALY_MIN_INTERVAL_MS,
  TelemetrySampler,
} from './telemetry/telemetrySampler'
import { getAudioSentryPort, type AudioSentryPort } from './audioSentryPort'
import type {
  LostPlayProbeRow,
  DeadAirEpisode,
  DeadAirFork,
  DeadAirSink,
  DeadPeer,
} from './deadAirTypes'
import { isIPadOS } from './iPadOS'
import { isWebKit } from './webkit'

/** Sampler event name for the recovery-nudge gate. */
const DEAD_AIR_NUDGE_EVENT = 'meeting_dead_air_nudge'

/**
 * The cadence at which a *continuing* episode can reach `onEpisode`.
 *
 * The nudge gate is a per-listener min-interval, so a consumer that debounces
 * episodes into a dialog sees the same set of dead peers no more often than
 * this however continuously the audio is dead. Any window a consumer applies
 * must be measured against this cadence, not assumed to be instantaneous — a
 * window narrower than one interval is unreachable no matter how long the
 * client stays deaf.
 *
 * The one thing that gets through faster is a peer the window has not seen yet;
 * see {@link AudioEpisodeSink.emit}. That can only shorten the gap between two
 * episodes, never the age of the run a consumer measures from its first one.
 */
export const DEAD_AIR_EPISODE_MIN_INTERVAL_MS = DEFAULT_ANOMALY_MIN_INTERVAL_MS

/**
 * audioEpisodeSink — where the dead-air detector's findings are written.
 *
 * - **GA** holds every confirmed episode: one `meeting_dead_air_scan` row of
 *   scalars, anomaly-gated per listener through the min-interval sampler
 *   (never 1-in-N). This is the population, and what BigQuery queries read.
 * - **Sentry** holds one rich bundle per dead-air dialog the user was shown:
 *   `captureMessage('meeting_dead_air_nudge')`, see {@link
 *   AudioEpisodeSink.captureNudge}. It carries the context ring, and the wiring
 *   and receiver snapshots of every escalated peer, which GA cannot hold.
 *   Sentry is reached through an injected port (see audioSentryPort).
 *
 * Episodes themselves no longer go to Sentry as messages. One went out per dead
 * peer, and most runs are short and clear on their own: in September 2026 that
 * was about 9,600 events from 7,000 users in two weeks, most of Sentry's
 * volume, with the rare useful bundle buried under it. A dialog is the point at
 * which a user has been deaf long enough to be told, so it is the one worth a
 * bundle. Each episode now leaves an `audio` breadcrumb instead, so the
 * dialog's bundle, or any error the client hits later, shows the run that led
 * up to it.
 *
 * Browser, version and OS are not stamped here — GA4 and Sentry both derive
 * them from the User-Agent. Two platform facts are, because neither sink can
 * work them out: `iPadOS`, because an iPad reports a desktop-Macintosh UA and
 * both would otherwise file it as a Mac, and `webKit`, because the engine is
 * what the wiring checks branch on and the User-Agent hides it in both
 * directions — an iPad files as a Mac, and Chrome for iOS files as Chrome. A
 * row that cannot say which side of that branch it came from cannot be read
 * back against a verdict the engine decided.
 */

/**
 * The context ring, as this sink reads it: one call, at emit time.
 *
 * Typed as a one-method port rather than the concrete buffer so the ~30s
 * context-capture machinery is optional. A sink with no ring still writes the
 * full episode to both destinations; it just carries no lead-up.
 */
export interface EpisodeContextRing {
  serialize(): string
}

/**
 * Which repair the dialog offers. Not chosen from the failure type — every
 * failure gets the same two rungs — but from how far up the ladder this client
 * has already climbed: `reconnect` until that has been spent, then `reload`.
 * Offering a reconnect to someone who has already tried one is the dead end
 * this replaces.
 */
export type DeadAirNudgeMode = 'reconnect' | 'reload'

/**
 * What the ladder hands the dialog when it escalates.
 *
 * `cause` is one fork; `forks` is every distinct fork in the escalated set,
 * deduped and sorted. They differ whenever a single episode escalates peers
 * that failed differently — peer A `not_attached`, peer B `delivery_stalled` —
 * and the dialog's events used to report only the first, so the calibration
 * could not see what else was wrong with a client it had nudged. The set is
 * carried here rather than looked up from the episode because escalation is
 * per peer: an episode's dead set and its escalated set are not the same list
 * once auto-heal has taken the fixable half.
 */
export interface DeadAirNudgeRequest {
  /** The fork to attribute the nudge to: the first escalated peer's. */
  cause: DeadAirFork
  /** Every distinct fork among the escalated peers, sorted. Includes `cause`. */
  forks: DeadAirFork[]
  /** The peers this client was deaf to, for the post-repair recovery probe. */
  identities: string[]
  /**
   * The escalated peers as one comparable `identity:fork` string — see
   * {@link buildDeadAirCondition}.
   *
   * Not `condition`, which on every other row is the whole episode. This is a
   * subset — peers whose cheap fix is spent or was never available — so the two
   * cannot be pooled: `COUNT(DISTINCT condition)` over both reads one fault as
   * two. It also cannot simply carry the episode's string instead, because a
   * peer whose fix fails escalates from the verify pass seconds later, with no
   * episode of its own to take one from.
   *
   * Carried for the telemetry, never for the cooldown: a nudge that is
   * suppressed writes what the client's fault was at the time, while the
   * decision to suppress reads {@link DeadAirNudgeRequest.identities} alone.
   */
  escalatedCondition: string
  /** Which rung to offer. */
  mode: DeadAirNudgeMode
}

/**
 * What the verify re-check could conclude about one cheap fix.
 *
 * Three values rather than a healed flag because the probe has three answers.
 * `pending` is the one a boolean cannot carry: the detector refuses to judge a
 * peer whose track is inside its attach-grace window, on the grounds that an
 * element which has not rendered yet is no evidence either way. Folding that
 * into `still_dead` told the calibration a fix had failed when nothing had
 * been measured, and biased the numbers against exactly the fixes whose settle
 * window happens to overlap a re-subscription.
 *
 * It is its own value, rather than a flag beside `healed`, so a healed-rate
 * cannot be computed without deciding what to do with it. A `pending` row is
 * still worth writing: how often the verify lands inside grace is what says
 * whether `autoHealSettleMS` is set too short against `attachGraceMS`.
 */
export type HealVerdict = 'healed' | 'still_dead' | 'pending'

/** One cheap auto-heal attempt's result, for calibration telemetry. */
export interface HealOutcome {
  identity: string
  fork: DeadAirFork
  /** The fix tried ('play' | 'resink'). */
  fix: string
  /** What the wiring re-check found after the fix. */
  verdict: HealVerdict
  /** Time from applying the fix to the verify re-check. */
  verifyLatencyMs: number
  /**
   * Whether the auto-heal cycle was still the one this fix was applied in when
   * the verify ran.
   *
   * False means something else re-armed the ladder in between — most often
   * another peer's dialog-driven reconnect, which rebuilds every subscription
   * in the room. The wiring then reads clean for reasons that have nothing to
   * do with this fix, so a `healed` verdict from that verify is not evidence
   * about the fix. The outcome is still recorded, because a natural recovery
   * inside the settle window is worth counting; this flag is what lets the
   * calibration separate the two instead of crediting the fix for both.
   */
  sameCycle: boolean
}

/** Whether the room is mid-reconnect, so subscriptions are expected to churn. */
export function isReconnecting(connectionState: string): boolean {
  return (
    connectionState === 'reconnecting' ||
    connectionState === 'signalReconnecting'
  )
}

/** ICE states in which media can actually flow. */
const HEALTHY_ICE_STATES = new Set(['connected', 'completed'])
/** `RTCPeerConnectionState` has no `completed`; `connected` is its only healthy value. */
const HEALTHY_PC_STATES = new Set(['connected'])

/**
 * Whether the subscriber transport was down when this episode was built.
 *
 * An allowlist rather than a blocklist, in both directions. A state we failed
 * to enumerate is treated as unhealthy, because the failure that produces is a
 * suppressed scan — re-checked by the backstop 30s later — while the opposite
 * mistake is the false roster-wide report this whole gate exists to stop.
 *
 * An absent state is the one case that reads healthy. Both reads behind
 * {@link DeadAirEnvironment.subscriberTransport} go through livekit internals
 * or a non-universal WebRTC surface, so a future SDK layout change costs us the
 * field. When that happens the detector should get noisier, not quieter: a
 * blind spot that reports too much is visible in the scan rows, and one that
 * reports nothing looks exactly like a fleet with no dead air.
 */
export function isTransportDown(
  episode: Pick<DeadAirEpisode, 'subscriberIceState' | 'subscriberPcState'>
): boolean {
  const { subscriberIceState: ice, subscriberPcState: pc } = episode
  if (ice !== undefined && !HEALTHY_ICE_STATES.has(ice)) return true
  if (pc !== undefined && !HEALTHY_PC_STATES.has(pc)) return true
  return false
}

/**
 * The failure type to record on an episode's nudge, or null when it isn't worth
 * one. The dialog does not branch on the value — it runs one repair and stamps
 * this on the heal events so the telemetry still says what was being repaired.
 *
 * A backgrounded tab pauses its <audio> elements (-> lost_play) and a
 * reconnecting room drops subscriptions (-> not_subscribed); both resolve on
 * their own, so nudging the user to repair them is wrong. A dead subscriber
 * transport is the third of these and the least visible: the room can read
 * `connected` right through it (see {@link DeadAirEpisode.subscriberIceState}),
 * so neither check above catches it, and every peer's element starves at once.
 * The cheap fix matched to the failure it produces is `play`, which cannot move
 * a starved element, so the ladder spends every peer's one attempt and hands
 * the whole roster to the dialog for a fault livekit is already repairing.
 * Otherwise take the first dead peer's failure type.
 */
export function resolveNudgeCause(episode: DeadAirEpisode): DeadAirFork | null {
  if (episode.pageHidden) return null
  if (isReconnecting(episode.connectionState)) return null
  if (isTransportDown(episode)) return null
  return episode.deadPeers[0]?.fork ?? null
}

/**
 * GA string values are capped at 100 chars.
 *
 * These fields are comma-joined lists, so a blind slice ends the value on a
 * half-identity (`peerA,peerB,peer`) that matches nothing and groups as its own
 * bucket in aggregate queries. Cutting back to the last separator inside the
 * limit keeps every entry that survives whole. A single entry longer than the
 * cap has no separator to fall back to and is sliced as before.
 */
/**
 * The `lostPlayKind` of every dead peer, comma-joined in `deadPeers` order, so
 * index i pairs with index i of the `deadPeers` and `forks` columns. Empty for
 * a peer with no kind.
 */
function lostPlayKinds(episode: DeadAirEpisode): string {
  return episode.deadPeers.map((p) => p.lostPlayKind ?? '').join(',')
}

export const truncate = (s: string, max = 100): string => {
  if (s.length <= max) return s
  const cut = s.slice(0, max)
  const lastComma = cut.lastIndexOf(',')
  return lastComma === -1 ? cut : cut.slice(0, lastComma)
}

/**
 * The episode's condition: every dead peer as `identity:fork`, sorted and
 * comma-joined (`peerA:lost_play,peerB:not_attached`).
 *
 * Sorted so the scan order the roster walk happened to condemn peers in cannot
 * make one condition read as two.
 *
 * A string rather than a hash, which is what the original proposal asked for.
 * The only operation performed on it is equality, and a string does equality
 * just as well while also saying what the condition was — where a responder
 * holding `a3f9c1` has to find a second row before the value means anything.
 * There is nothing to protect either: the same peer identities and forks are
 * already emitted raw in the `deadPeers` and `forks` columns of every scan row.
 *
 * What it answers: forty `meeting_dead_air` rows for one student are ambiguous
 * between one fault that never healed and forty separate faults, and those call
 * for opposite responses. `COUNT(DISTINCT condition)` settles it.
 */
export function buildDeadAirCondition(
  peers: readonly { identity: string; fork: DeadAirFork }[]
): string {
  return peers
    .map((p) => `${p.identity}:${p.fork}`)
    .sort()
    .join(',')
}

/**
 * 32-bit FNV-1a, as eight hex characters.
 *
 * Here to give {@link truncateCondition} something short and stable to append,
 * and used for nothing else. Not a security boundary and not trying to be: the
 * identities it digests are already emitted raw in the `deadPeers` column
 * beside it, so there is nothing here an attacker could not read off the same
 * row.
 */
function fnv1a(s: string): string {
  let hash = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    hash ^= s.charCodeAt(i)
    // The 32-bit FNV prime, as shifts, because a plain multiply overflows the
    // float53 mantissa and stops being the same function on long inputs.
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24)
    hash >>>= 0
  }
  return hash.toString(16).padStart(8, '0')
}

/**
 * Cap a condition at the GA limit without costing it its identity as an
 * equality key.
 *
 * Plain {@link truncate} is right for `deadPeers` and `forks`, which are read
 * as "who and what, roughly", and wrong here. A condition entry is a 28-char
 * uid plus a fork, so it runs 38 to 47 characters: two entries always fit under
 * the cap and three never do. Every episode with three or more dead peers
 * therefore emits exactly its first two sorted entries. Because the sort is
 * lexicographic on identities that are stable for the life of a room, those two
 * are usually the same peers throughout an incident, so a third peer joining
 * the dead set — or peer C moving from `not_subscribed` to `lost_play` — leaves
 * the value byte-identical. `COUNT(DISTINCT condition)` then reads two faults
 * as one, which is the exact question the field exists to answer.
 *
 * The suffix carries what the cut threw away: how many peers went missing, and
 * a digest of the whole condition. Equality on the emitted value is equality on
 * the full one, while the head still names the two peers a responder would
 * chase first. A digest alone would have been cheaper and was rejected for the
 * reason on {@link buildDeadAirCondition}: `a3f9c1` means nothing until you
 * have found a second row carrying it.
 *
 * Raising the cap is not available. GA4 cuts a string parameter at 100
 * characters wherever it lands, and a value cut there arrives ending on a
 * half-identity.
 */
export function truncateCondition(condition: string, max = 100): string {
  if (condition.length <= max) return condition
  const digest = fnv1a(condition)
  const total = condition.split(',').length
  // Reserved against `total` rather than the count actually dropped, which is
  // not known until the head has been cut. Dropped is never larger than total,
  // so the reservation is never short and the result cannot overrun the cap.
  const head = truncate(condition, max - `+${total}~${digest}`.length)
  return `${head}+${total - head.split(',').length}~${digest}`
}

/**
 * Run an app-supplied side effect, swallowing anything it throws. Returns the
 * value, or undefined if it threw — see {@link AudioEpisodeSink.emit}.
 */
function guarded<T>(fn: () => T): T | undefined {
  try {
    return fn()
  } catch {
    return undefined
  }
}

export interface AudioEpisodeSinkDeps {
  /** GA transport (repository.logEvent). */
  logEvent: (name: string, params: Record<string, unknown>) => void
  /** The min-interval anomaly sampler (shared with the detector). */
  sampler: TelemetrySampler
  /** The context ring; serialized into the nudge bundle when one is wired. */
  ring?: EpisodeContextRing
  /** Lazy Sentry-port accessor (defaults to the registered global port). */
  sentry?: () => AudioSentryPort | null
  /** Injectable clock (defaults to Date.now). */
  now?: () => number
  /**
   * Called once per confirmed episode, after the GA row and the breadcrumb.
   * The controller uses this to drive the user-facing recovery nudge; the sink
   * stays free of any cause/heal policy.
   */
  onEpisode?: (episode: DeadAirEpisode) => void
}

export interface ReloadFlushOptions {
  /** Passed to Sentry.flush — how long to wait for the transport to drain. */
  flushTimeoutMs?: number
  /**
   * Only flush when a Sentry message was captured within this window before
   * the page left; an unrelated reload long after the last one needs no flush.
   */
  pendingWindowMs?: number
}

const DEFAULT_FLUSH_TIMEOUT_MS = 2000
const DEFAULT_PENDING_WINDOW_MS = 8000

export class AudioEpisodeSink implements DeadAirSink {
  private readonly deps: AudioEpisodeSinkDeps
  private readonly sentry: () => AudioSentryPort | null
  private readonly now: () => number
  /**
   * When the last Sentry message (nudge or heal) was captured, for the reload
   * flush. Episodes do not set it: they no longer produce a Sentry message, so
   * there is nothing of theirs in the transport to save.
   */
  private lastEmitAt: number | null = null
  /**
   * The most recent episode, for the nudge bundle's episode-wide tags. The
   * nudge fires from an episode that has just passed through here, so this is
   * normally the one that caused it.
   */
  private lastEpisode: { episode: DeadAirEpisode; at: number } | null = null
  /**
   * Per peer, the last dead-peer record any episode carried. Kept per peer
   * rather than read off {@link lastEpisode} because a peer can escalate from
   * the heal verify pass seconds after its episode, by which time a later
   * episode may no longer list it. Never cleared, like {@link lastHeal}.
   */
  private readonly lastPeers = new Map<string, { peer: DeadPeer; at: number }>()
  /**
   * What the last episode breadcrumb described, so a changed condition is
   * recorded at once instead of waiting out the breadcrumb interval.
   */
  private lastBreadcrumbCondition: string | null = null
  /**
   * The identities already handed to `onEpisode` inside the current nudge
   * window. Replaced whenever the sampler opens a new one.
   */
  private nudgedPeers = new Set<string>()
  /**
   * Per peer, what its last cheap fix achieved (`'<fix>:<verdict>'`), so the
   * next episode for that peer can say so on the report itself. Keyed by
   * identity and never cleared: a room's roster is small, and the value is
   * overwritten by the next attempt.
   */
  private readonly lastHeal = new Map<string, string>()
  private reloadFlushCleanup: (() => void) | null = null

  constructor(deps: AudioEpisodeSinkDeps) {
    this.deps = deps
    this.sentry = deps.sentry ?? getAudioSentryPort
    this.now = deps.now ?? Date.now
  }

  /**
   * Record a confirmed episode: the GA row, an `audio` breadcrumb, and the
   * snapshots a later nudge bundle will need. No-op when there are no dead
   * peers — GA only fires when peersDead > 0, and there is nothing to record
   * otherwise.
   *
   * Every step is individually guarded. This runs inside a livekit
   * `trackSubscribed` listener and inside the slide transition, neither of
   * which catches: a throw from the GA transport, the Sentry port or the nudge
   * consumer would abort the remaining livekit listeners for that event — which
   * is how AudioObservabilitySources attaches its element listeners — or
   * propagate out of the slide change. Diagnostics must never break the thing
   * they are measuring, least of all at the moment they exist to observe.
   */
  emit(episode: DeadAirEpisode): void {
    const peersDead = episode.deadPeers.length
    if (peersDead === 0) return

    // Stored before anything that can throw, and ungated: the nudge this
    // episode may lead to reads it, and the sampler gates only what is sent.
    const at = this.now()
    this.lastEpisode = { episode, at }
    for (const peer of episode.deadPeers) {
      this.lastPeers.set(peer.identity, { peer, at })
    }

    guarded(() => this.emitScanRow(episode, peersDead))
    guarded(() => this.emitBreadcrumb(episode, peersDead))
    // After the GA row and the breadcrumb: hand the episode to the recovery
    // nudge. Gated
    // on its own key so a stuck peer can't re-offer the dialog on every
    // backstop sweep and slide change, and so the throttle it shares with
    // nothing else can't be spent by the GA or breadcrumb paths.
    const nudgeKey = TelemetrySampler.anomalyKey(
      DEAD_AIR_NUDGE_EVENT,
      episode.listenerId
    )
    // Deliberately a flat interval, not backoff: the UI decides which episodes
    // belong to the same run by measuring gaps against
    // DEAD_AIR_EPISODE_MIN_INTERVAL_MS, so stretching this gap would split one
    // continuous episode into runs that never reach the wait duration.
    //
    // The key is per-listener, but this hook drives the auto-heal ladder as
    // well as the dialog, and the ladder is per-peer. A flat listener gate
    // therefore swallowed the whole repair for anyone who broke second: peer A
    // breaks on a slide change and is fixed, peer B breaks five seconds later
    // on trackSubscribed and gets nothing until the interval elapses. So a peer
    // this window has not seen yet passes even when the gate is shut. Each
    // identity buys exactly one such pass per window — a set that flaps between
    // two peers can't ride the exception every cycle.
    const identities = episode.deadPeers.map((p) => p.identity)
    if (this.deps.sampler.allowAnomaly(nudgeKey)) {
      this.nudgedPeers = new Set(identities)
    } else if (identities.every((id) => this.nudgedPeers.has(id))) {
      return
    } else {
      for (const id of identities) this.nudgedPeers.add(id)
    }
    guarded(() => this.deps.onEpisode?.(episode))
  }

  /**
   * Reload survival: the nudge and heal bundles are client-side Sentry events,
   * and the dominant coping behavior in the corpus is an immediate page reload
   * — the dialog's own reload rung is one — which can drop the event before
   * its async transport leaves. Register a `pagehide` /
   * `visibilitychange → hidden` handler that flushes Sentry when a fresh
   * message is still in flight. Firebase Analytics already flushes its
   * own queue on unload, so only the Sentry side needs an explicit flush here.
   *
   * Returns a cleanup fn; also stored so {@link removeReloadFlush} can undo it.
   *
   * Installing twice is a no-op that hands back the standing cleanup. Only one
   * cleanup can be tracked, so a second install would strand the first pair of
   * listeners: `removeReloadFlush` would unwind the newer pair and leave the
   * older one attached for the life of the page. The listeners are idempotent
   * on the flush itself, so the cost is a leak rather than a double-flush, but
   * a re-mount or an HMR pass is enough to hit it.
   */
  installReloadFlush(options: ReloadFlushOptions = {}): () => void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return () => {}
    }
    if (this.reloadFlushCleanup) return this.reloadFlushCleanup
    const flushTimeout = options.flushTimeoutMs ?? DEFAULT_FLUSH_TIMEOUT_MS
    const windowMs = options.pendingWindowMs ?? DEFAULT_PENDING_WINDOW_MS

    const flushIfPending = () => {
      if (this.lastEmitAt === null) return
      if (this.now() - this.lastEmitAt > windowMs) return
      // Guarded on both axes, the same rule the rest of the file follows: a
      // port that throws synchronously must not break `pagehide`, and a flush
      // that rejects must not surface as an unhandled rejection during unload,
      // which some browsers report as a page error. Nothing reads the result —
      // there is no recovery from a failed flush at this point in the page's
      // life, only the choice not to make it someone else's error.
      guarded(() => {
        void this.sentry()
          ?.flush(flushTimeout)
          .catch(() => {})
      })
    }
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushIfPending()
    }

    window.addEventListener('pagehide', flushIfPending)
    document.addEventListener('visibilitychange', onVisibility)
    const cleanup = () => {
      window.removeEventListener('pagehide', flushIfPending)
      document.removeEventListener('visibilitychange', onVisibility)
      this.reloadFlushCleanup = null
    }
    this.reloadFlushCleanup = cleanup
    return cleanup
  }

  removeReloadFlush(): void {
    this.reloadFlushCleanup?.()
  }

  /**
   * One `meeting_dead_air_probe` GA row per finished stats probe. GA only:
   * this is telemetry for a later decision, not something to alert on.
   *
   * Throttled per listener and peer on the episode interval, the same gate
   * the scan row uses, except when the detector asks to bypass it: the row
   * where a peer first reads flat on two sweeps running is what the readout
   * counts, and an interval that happened to be closed must not drop it.
   *
   * 17 params, plus `logGaEvent`'s five, is 22 of GA4's 25.
   */
  emitProbe(
    row: LostPlayProbeRow,
    options: { bypassThrottle?: boolean } = {}
  ): void {
    guarded(() => this.emitProbeRow(row, options.bypassThrottle === true))
  }

  private emitProbeRow(row: LostPlayProbeRow, bypassThrottle: boolean): void {
    const key = TelemetrySampler.anomalyKey(
      'meeting_dead_air_probe',
      row.listenerId,
      row.peer
    )
    if (bypassThrottle) this.deps.sampler.restartAnomaly(key)
    else if (!this.deps.sampler.allowAnomaly(key)) return

    this.deps.logEvent('meeting_dead_air_probe', {
      trigger: row.trigger,
      peer: truncate(row.peer),
      lostPlayKind: row.lostPlayKind,
      currentTimeFrozen: row.currentTimeFrozen,
      rtpArriving: row.rtpArriving,
      samplesPulled: row.samplesPulled,
      flatStreak: row.flatStreak,
      packetsDelta: row.packetsDelta,
      samplesDelta: row.samplesDelta,
      concealedDelta: row.concealedDelta,
      probeGapMs: row.probeGapMs,
      pageHidden: row.pageHidden,
      transportDown: isTransportDown(row),
      connectionState: row.connectionState,
      healRan: row.healRan,
      webKit: isWebKit(),
      detectorVersion: row.detectorVersion,
    })
  }

  /**
   * Record the outcome of one cheap auto-heal attempt: which failure was hit,
   * which fix was tried, whether it healed the peer, and how long the verify
   * took. This is how we tell a fix that works from one that quietly makes
   * things worse before trusting auto-heal — so it is emitted whether or not the
   * fix worked. Guarded like emit(): diagnostics must never break the room.
   *
   * Three destinations, and the Sentry message is the one that has to exist. A
   * peer the fix repaired never reaches a dialog, so no nudge bundle ever
   * carries its breadcrumb: on the breadcrumb path a *successful* heal is
   * invisible by construction. Its own message is not. A failed heal does
   * reach the dialog, and its breadcrumb rides on that bundle.
   *
   * Ungated by the sampler, unlike the episode messages. A peer's cheap fix is
   * spent once per cycle and a cycle re-arms only on a genuine recovery, so the
   * volume is bounded by how often the client actually recovers — and a
   * throttle here would drop exactly the confirmations this exists to collect.
   */
  emitHealOutcome(outcome: HealOutcome): void {
    this.lastHeal.set(outcome.identity, `${outcome.fix}:${outcome.verdict}`)
    // Marked for the reload flush: a heal that fails escalates to a dialog
    // whose reload button can take the page down before this message leaves.
    this.lastEmitAt = this.now()
    guarded(() =>
      this.sentry()?.captureMessage({
        message: 'meeting_dead_air_heal',
        level: outcome.verdict === 'still_dead' ? 'warning' : 'info',
        tags: {
          peer: outcome.identity,
          fork: outcome.fork,
          fix: outcome.fix,
          verdict: outcome.verdict,
          sameCycle: outcome.sameCycle,
          iPadOS: isIPadOS(),
          webKit: isWebKit(),
        },
        contexts: { heal: { verifyLatencyMs: outcome.verifyLatencyMs } },
        // Grouped by what was tried and what it achieved, so "does resink ever
        // work" is one issue to read rather than a search across every peer.
        fingerprint: ['meeting_dead_air_heal', outcome.fix, outcome.verdict],
      })
    )
    guarded(() =>
      this.deps.logEvent('meeting_dead_air_heal', {
        fork: outcome.fork,
        fix: outcome.fix,
        verdict: outcome.verdict,
        verifyLatencyMs: outcome.verifyLatencyMs,
        sameCycle: outcome.sameCycle,
        iPadOS: isIPadOS(),
        webKit: isWebKit(),
      })
    )
    guarded(() =>
      this.sentry()?.addBreadcrumb({
        category: 'audio',
        message: 'meeting_dead_air_heal',
        // Only a fix that was measured and failed is a warning. `pending` is
        // an unmeasured attempt, and filing it at the same level as a failure
        // is what the third verdict exists to stop.
        level: outcome.verdict === 'still_dead' ? 'warning' : 'info',
        data: {
          identity: outcome.identity,
          fork: outcome.fork,
          fix: outcome.fix,
          verdict: outcome.verdict,
          verifyLatencyMs: outcome.verifyLatencyMs,
          sameCycle: outcome.sameCycle,
        },
      })
    )
  }

  /**
   * GA aggregate matrix row — scalars only, one per scan.
   *
   * Watch the parameter count. GA4 caps an event at 25 parameters and silently
   * drops the overflow, and this row is not the whole budget: `logGaEvent` adds
   * five identifiers (room, assignment, section, slide deck, active slide)
   * before it is sent. As of this comment the row is 20 at its widest — the 18
   * fields below, plus `fork` on a single-peer scan and `detectorVersion` —
   * which is 25 sent, exactly the cap, with no spare. A field added here
   * without dropping one is likely to be the one that goes missing, and
   * nothing reports it.
   */
  private emitScanRow(episode: DeadAirEpisode, peersDead: number): void {
    const scanKey = TelemetrySampler.anomalyKey(
      'meeting_dead_air_scan',
      episode.listenerId
    )
    if (!this.deps.sampler.allowAnomaly(scanKey)) return

    const params: Record<string, unknown> = {
      peersDead,
      peersSubscribed: episode.peersSubscribed,
      peersPublishing: episode.peersPublishing,
      pageHidden: episode.pageHidden,
      // Every half of the nudge-cause gate, so a row says whether the recovery
      // ladder ran. `resolveNudgeCause` returns null for a hidden page, a
      // reconnecting room or a dead subscriber transport and `onEpisode`
      // returns on that, which means no repair is attempted and no dialog is
      // offered — and without these the row is identical to one whose fix ran
      // and healed the peer silently.
      connectionState: episode.connectionState,
      // Not derivable from connectionState: the room reads `connected` through
      // a dead transport, which is the case this field exists to name. Any
      // rate computed over these rows that does not split on it is counting
      // transport outages as local playback faults.
      transportDown: isTransportDown(episode),
      subscriberIceState: episode.subscriberIceState ?? '',
      secondsSinceSlideChange: episode.secondsSinceSlideChange ?? -1,
      triggeringSpeaker: episode.triggeringSpeaker ?? '',
      // Deliberately raw, in roster order, and deliberately not the same shape
      // as the Sentry `forks` tags, which are deduped and sorted. These two are
      // positionally paired: index i of `forks` is the failure type of index i
      // of `deadPeers`, which is the only way a GA row of scalars can say which
      // peer hit which fault. Deduping would destroy that pairing on every row.
      // The Sentry side is a queryable tag rather than a list, so there the
      // stable deduped form is the useful one. The pairing degrades if either
      // value overruns the 100-char cap and truncates to a different count —
      // acceptable, because peersDead is 1-3 in the cases these rows answer.
      deadPeers: truncate(episode.deadPeers.map((p) => p.identity).join(',')),
      forks: truncate(episode.deadPeers.map((p) => p.fork).join(',')),
      // Paired the same way, with an empty entry for every peer that is not
      // `lost_play` or whose kind could not be read.
      lostPlayKinds: truncate(lostPlayKinds(episode)),
      // How long this client has been deaf in the current run. -1 rather than
      // an absent param when the detector did not stamp one, so a row that
      // predates the field is not read as a run that had just started.
      deadAirDurationMs: episode.deadAirDurationMs ?? -1,
      // Not truncated the way `deadPeers` and `forks` are. Those are read as
      // "who and what, roughly", and losing a tail entry costs them nothing.
      // This one is an equality key, so it keeps a digest of what the cut
      // dropped. See `truncateCondition`.
      condition: truncateCondition(buildDeadAirCondition(episode.deadPeers)),
      // The envelope the fault sits in. Empty and -1 rather than absent, so a
      // row where the read failed is visible as such instead of silently
      // dropping out of a GROUP BY.
      livekitConnectionQuality: episode.livekitConnectionQuality ?? '',
      roomParticipantCount: episode.roomParticipantCount ?? -1,
      iPadOS: isIPadOS(),
      webKit: isWebKit(),
    }
    // Pairwise convenience: single-peer scans carry the failure type directly.
    if (peersDead === 1) params.fork = episode.deadPeers[0].fork
    // Written only when the detector stamped one, so an absent value keeps
    // meaning "emitted before this field existed" rather than being pooled with
    // version 1.
    if (episode.detectorVersion !== undefined) {
      params.detectorVersion = episode.detectorVersion
    }
    this.deps.logEvent('meeting_dead_air_scan', params)
  }

  /**
   * One `audio` breadcrumb per episode, so a nudge bundle shows the whole run
   * that led to the dialog rather than only the ring's last ~30 seconds.
   *
   * Scalars only: the breadcrumb ring holds 100 entries for the whole page and
   * is shared with everything else the client logs. Gated per listener on the
   * episode interval, the same as the scan row, but a changed condition or
   * transport verdict goes through at once: that is a different fault, and the
   * point of the trail is to show when the fault changed.
   */
  private emitBreadcrumb(episode: DeadAirEpisode, peersDead: number): void {
    const port = this.sentry()
    if (!port) return
    const transportDown = isTransportDown(episode)
    const condition = truncateCondition(
      buildDeadAirCondition(episode.deadPeers)
    )
    const signature = `${condition}|${transportDown}`
    const key = TelemetrySampler.anomalyKey(
      'meeting_dead_air_breadcrumb',
      episode.listenerId
    )
    const changed = signature !== this.lastBreadcrumbCondition
    const allowed = this.deps.sampler.allowAnomaly(key)
    if (!allowed && !changed) return
    // A changed fault passes a closed gate. Restart the interval from it, or
    // the next unchanged episode passes as soon as the old interval runs out
    // and adds a second breadcrumb for the same fault.
    if (!allowed) this.deps.sampler.restartAnomaly(key)
    this.lastBreadcrumbCondition = signature

    port.addBreadcrumb({
      category: 'audio',
      message: 'meeting_dead_air',
      level: 'warning',
      data: {
        peersDead,
        forks: [...new Set(episode.deadPeers.map((p) => p.fork))]
          .sort()
          .join(','),
        lostPlayKinds: lostPlayKinds(episode),
        condition,
        transportDown,
        connectionState: episode.connectionState,
        pageHidden: episode.pageHidden,
        deadAirDurationMs: episode.deadAirDurationMs ?? -1,
      },
    })
  }

  /**
   * The Sentry bundle for a dead-air dialog the user was actually shown. The
   * page calls this, through the controller, at the moment the dialog opens;
   * a nudge that was suppressed or never displayed sends nothing.
   *
   * One message per dialog, not per peer. The escalated peers' evidence rides
   * in contexts keyed by identity, and the episode-wide state comes from the
   * last episode this sink recorded, which is the one that led here.
   *
   * Grouped by cause and rung. A `reload` dialog means a reconnect was already
   * spent on this user and they are still deaf, which is the worst outcome the
   * ladder has, so it is its own issue rather than a tag inside the first-try
   * population.
   *
   * Guarded like everything else here: this runs inside a React effect on the
   * meeting page, and a diagnostics failure must not stop the dialog.
   */
  captureNudge(request: DeadAirNudgeRequest): void {
    guarded(() => this.sendNudge(request))
  }

  private sendNudge(request: DeadAirNudgeRequest): void {
    const port = this.sentry()
    if (!port) return
    const now = this.now()
    const last = this.lastEpisode
    const episode = last?.episode

    const escalated = request.identities.map((identity) => {
      const record = this.lastPeers.get(identity)
      return { identity, peer: record?.peer, at: record?.at }
    })

    const tags: Record<string, string | number | boolean> = {
      iPadOS: isIPadOS(),
      webKit: isWebKit(),
      cause: request.cause,
      mode: request.mode,
      forks: request.forks.join(','),
      peersEscalated: request.identities.length,
      // Truncated by the same function as every other copy, so it compares
      // equal to the `escalatedCondition` on the GA nudge rows.
      escalatedCondition: truncateCondition(request.escalatedCondition),
      // What each escalated peer's last cheap fix achieved, deduped and
      // sorted: `none` means no repair was tried for any of them.
      lastHeal: [
        ...new Set(
          request.identities.map((id) => this.lastHeal.get(id) ?? 'none')
        ),
      ]
        .sort()
        .join(','),
      // -1 when no episode was recorded, so the absence is visible.
      episodeAgeMs: last ? now - last.at : -1,
    }
    // A tag only when every escalated `lost_play` peer has the same kind: a
    // mixed or unread set has no single value to search on, and each peer's
    // own kind is in the `escalated` context either way.
    const kinds = new Set(
      escalated
        .filter(({ peer }) => peer?.fork === 'lost_play')
        .map(({ peer }) => peer?.lostPlayKind)
    )
    const [onlyKind] = kinds
    if (kinds.size === 1 && onlyKind !== undefined) {
      tags.lostPlayKind = onlyKind
    }
    if (episode) {
      Object.assign(tags, {
        peersDead: episode.deadPeers.length,
        // The whole episode, as opposed to the escalated subset above. Two
        // different populations, so two names; see DeadAirNudgeRequest.
        condition: truncateCondition(buildDeadAirCondition(episode.deadPeers)),
        pageHidden: episode.pageHidden,
        connectionState: episode.connectionState,
        transportDown: isTransportDown(episode),
        subscriberIceState: episode.subscriberIceState ?? '',
        subscriberPcState: episode.subscriberPcState ?? '',
        deadAirDurationMs: episode.deadAirDurationMs ?? -1,
        livekitConnectionQuality: episode.livekitConnectionQuality ?? '',
        roomParticipantCount: episode.roomParticipantCount ?? -1,
      })
      // Absent, not defaulted, for the same reason it is on the GA row.
      if (episode.detectorVersion !== undefined) {
        tags.detectorVersion = episode.detectorVersion
      }
    }

    // Keyed by identity one level down from the context, which keeps an
    // element's `sinkId` at the depth `normalizeDepth: 6` in apps/web was set
    // for: contexts > wiring > identity > elements > element.
    const wiring: Record<string, unknown> = {}
    const receiverStats: Record<string, unknown> = {}
    for (const { identity, peer } of escalated) {
      wiring[identity] = peer?.wiring ?? null
      // `ageMs` is not decoration: a structural fault carries the last sample
      // the flow checks read rather than one of its own. 0 means read to
      // reach this verdict; null means no sample travelled with the episode.
      receiverStats[identity] = {
        snapshot: peer?.receiverStats ?? null,
        ageMs: peer?.receiverStats ? (peer.receiverStatsAgeMs ?? null) : null,
      }
    }

    const contexts: Record<string, Record<string, unknown>> = {
      escalated: {
        list: escalated.map(({ identity, peer, at }) => ({
          identity,
          fork: peer?.fork ?? null,
          lostPlayKind: peer?.lostPlayKind ?? null,
          deadForMs: peer?.deadForMs ?? -1,
          lastHeal: this.lastHeal.get(identity) ?? 'none',
          // How old this peer's record is. Normally the episode that just
          // fired; older when the peer escalated from a heal verify pass.
          recordAgeMs: at === undefined ? null : now - at,
        })),
      },
      wiring,
      receiverStats,
    }
    if (episode) {
      // The full dead-peer roster, which can be wider than the escalated set
      // once auto-heal has taken the fixable half.
      contexts.deadPeers = {
        list: episode.deadPeers.map((p) => ({
          identity: p.identity,
          fork: p.fork,
        })),
      }
    }
    // Stringified so Sentry's normalizeDepth/maxValueLength can't mangle it.
    // Read now rather than at the episode, so it holds the failed heal too.
    const audioRing = guarded(() => this.deps.ring?.serialize())
    if (audioRing !== undefined) contexts.audioRing = { entries: audioRing }

    // Marked before the send: the dialog's reload button can take the page
    // down seconds from now, and the flush on the way out is what saves this.
    this.lastEmitAt = now
    port.captureMessage({
      message: 'meeting_dead_air_nudge',
      level: 'warning',
      tags,
      contexts,
      fingerprint: ['meeting_dead_air_nudge', request.cause, request.mode],
    })
  }
}
